import { inject, Injectable } from '@angular/core';
import { catchError, filter, map, Observable, take, throwError, throwIfEmpty } from 'rxjs';

import { DpuFileSystemClient } from 'shared-business/file-system';
import { FileItem as DpuFileItem, FileSort, StorageItem } from 'shared-utils/file-system';

import { DPU_SERVICE } from '../../dpu-file';
import { DataEnum, FileItem, FileVersion } from '../models';
import { FileBrowserBaseService } from './file-browser-base.service';

const DEFAULT_SORT: FileSort = { type: 'filename', direction: 'asc' };

@Injectable()
export class LocalFileBrowserService extends FileBrowserBaseService {
  private readonly _dpuFileSystemClient = new DpuFileSystemClient(inject(DPU_SERVICE));

  /**
   * Local-only call listing the available storage locations as browsable root folders.
   * @returns {Observable<FileItem[]>} the storage locations mapped to root folders
   */
  public getStorages(): Observable<FileItem[]> {
    return this._dpuFileSystemClient.getStorageLocations().pipe(
      take(1),
      filter((storages: StorageItem[]) => storages.length > 0),
      throwIfEmpty(),
      map((storages: StorageItem[]) => storages.map(storage => this.toStorageFolder(storage))),
      // Fallback to user files paths when there are no storage locations, could happen when there are no /mnt/ or /run/media/ on linux.
      // Temporary until the DPU commands does not explicitly filter out the storage locations for ACE.
      catchError(() => this.getStoragesFromUserFilesPaths()),
    );
  }

  /**
   * Set the root directory in state. It will appear in first item of breadcrumb with the "root" property to true.
   * @param {{ name: string, id?: string }} root - definition of the root. The name is required, the id is the storage path
   * @returns {void}
   */
  public setRoot({ name, id }: { name: string, id?: string }): void {
    const path: string = id ?? '';
    this._state.next({
      ...this._state.value,
      root: { name, id: path, type: DataEnum.FOLDER, tags: {}, latestVersion: this.emptyVersion(path), root: true },
    });
  }

  /**
   * Load items for a specific folder or root.
   * It will toggle the loading during the call
   * @param {FileItem} [parent = null] - will load items from this folder. Keep it empty to load from root
   * @return {Observable<void>} nothing if succeeded, throw error if the call fails
   */
  public loadItems(parent: FileItem | null = null): Observable<void> {
    this._state.next({ ...this._state.value, loading: true });

    const folder: FileItem | null = parent ?? this._state.value.root;

    return this._dpuFileSystemClient.getFiles(folder?.id ?? '', '', DEFAULT_SORT, undefined).pipe(
      take(1),
      map((files: DpuFileItem[]) => {
        this.loadItemsSuccess(files.map(file => this.toFileItem(file)));
      }),
      catchError((error: unknown) => {
        this.loadItemsFail();

        return throwError(() => error);
      }),
    );
  }

  private getStoragesFromUserFilesPaths(): Observable<FileItem[]> {
    return this._dpuFileSystemClient.getUserFilesPaths().pipe(
      take(1),
      map((userFilesPaths) => {
        const location = userFilesPaths.find(path => path.type === 'root')?.path;

        if (!location) {
          throw new Error('[LocalFileBrowserService] Storage locations & No root path found');
        }

        return [this.toStorageFolder({
          id: location,
          type: 'fixed',
          title: location,
        })];
      }),
    );
  }

  private toFileItem(file: DpuFileItem): FileItem {
    return {
      id: file.id,
      name: file.label,
      type: file.type === 'folder' ? DataEnum.FOLDER : DataEnum.FILE,
      tags: {},
      parentId: file.parentId ?? undefined,
      createdAt: file.metadata.createdAt,
      updatedAt: file.metadata.updatedAt,
      latestVersion: { ...this.emptyVersion(file.id), sizeInBytes: Number(file.metadata.size) || 0 },
    };
  }

  private toStorageFolder(storage: StorageItem): FileItem {
    return {
      id: storage.id,
      name: storage.title,
      type: DataEnum.FOLDER,
      tags: {},
      latestVersion: this.emptyVersion(storage.id),
      root: true,
    };
  }

  private emptyVersion(fileItemId: string): FileVersion {
    return { id: '', fileItemId, contentHash: '', contentType: '', sizeInBytes: 0 };
  }
}
